import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  FiSearch, FiPlus, FiTrash2, FiShoppingBag, FiCheckCircle,
  FiChevronLeft, FiChevronRight, FiEdit2, FiPackage, FiBarChart2,
  FiArrowDownCircle, FiArrowUpCircle,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { storeAPI, storeItemAPI } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';

const TABS = [
  { key: 'purchase', label: 'Purchased Items' },
  { key: 'returnable', label: 'Returnable Items' },
  { key: 'issue', label: 'Non-Paid / Issued Items' },
];

const PERSON_TYPES = ['Student', 'Teacher', 'Staff'];

const emptyForm = {
  itemName: '', personName: '', personType: 'Student', quantity: 1,
  price: '', isPaid: true, date: '', issueDate: '', expectedReturnDate: '', remarks: '',
};

const emptyItemForm = { itemName: '', unit: '', openingStock: 0, openingStockDate: '', reorderLevel: '' };
const emptyMovementForm = { item: '', type: 'Received', quantity: '', date: '', issuedTo: '', remarks: '' };

const inputClass =
  'w-full px-4 py-2.5 border border-blue-100 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200 text-slate-700';
const labelClass =
  'block text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1.5';

const AdminStore = () => {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'store_management';

  const [section, setSection] = useState('transactions'); // 'transactions' | 'inventory'

  const [activeTab, setActiveTab] = useState('purchase');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [personTypeFilter, setPersonTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 15;

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // ── Inventory & Stock ──────────────────────────────────────────
  const [invSubTab, setInvSubTab] = useState('items'); // 'items' | 'movements' | 'report'

  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [itemForm, setItemForm] = useState(emptyItemForm);
  const [editingItem, setEditingItem] = useState(null);
  const [savingItem, setSavingItem] = useState(false);

  const [movements, setMovements] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(true);
  const [movementModalOpen, setMovementModalOpen] = useState(false);
  const [movementForm, setMovementForm] = useState(emptyMovementForm);
  const [savingMovement, setSavingMovement] = useState(false);

  const [reportFrom, setReportFrom] = useState('');
  const [reportTo, setReportTo] = useState('');
  const [inventoryReport, setInventoryReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const fetchTransactions = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = { type: activeTab };
      if (search) params.search = search;
      if (personTypeFilter) params.personType = personTypeFilter;
      const res = await storeAPI.getAll(params);
      setTransactions(res.data?.data?.transactions || []);
      if (!silent) setPage(1);
    } catch {
      if (!silent) toast.error('Failed to load store records.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [activeTab, search, personTypeFilter]);

  useEffect(() => {
    const timer = setTimeout(() => fetchTransactions(), search ? 350 : 0);
    return () => clearTimeout(timer);
  }, [fetchTransactions, search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        transactionType: activeTab,
        itemName: form.itemName,
        personName: form.personName,
        personType: form.personType,
        quantity: Number(form.quantity) || 1,
        remarks: form.remarks,
        date: form.date || undefined,
      };
      if (activeTab === 'purchase') {
        payload.price = form.price !== '' ? Number(form.price) : undefined;
        payload.isPaid = form.isPaid;
      }
      if (activeTab === 'returnable') {
        payload.issueDate = form.issueDate || form.date || undefined;
        payload.expectedReturnDate = form.expectedReturnDate || undefined;
      }
      await storeAPI.create(payload);
      toast.success('Record saved.');
      setModalOpen(false);
      setForm(emptyForm);
      fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save record.');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkReturned = async (id) => {
    try {
      const res = await storeAPI.markReturned(id);
      const updated = res.data?.data?.transaction;
      setTransactions((prev) => prev.map((t) => (t._id === id ? { ...t, ...updated } : t)));
      toast.success('Item marked as returned.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update record.');
    }
  };

  const handleTogglePaid = async (id) => {
    try {
      const res = await storeAPI.togglePaid(id);
      const updated = res.data?.data?.transaction;
      setTransactions((prev) => prev.map((t) => (t._id === id ? { ...t, ...updated } : t)));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update paid status.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      await storeAPI.delete(id);
      setTransactions((prev) => prev.filter((t) => t._id !== id));
      toast.success('Record deleted.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete record.');
    }
  };

  const totalPages = Math.ceil(transactions.length / perPage);
  const paginated = transactions.slice((page - 1) * perPage, page * perPage);

  // ── Inventory & Stock: Items ────────────────────────────────────
  const fetchItems = useCallback(async (silent = false) => {
    if (!silent) setItemsLoading(true);
    try {
      const res = await storeItemAPI.getAll();
      setItems(res.data?.data?.items || []);
    } catch {
      if (!silent) toast.error('Failed to load items.');
    } finally {
      if (!silent) setItemsLoading(false);
    }
  }, []);

  const fetchMovements = useCallback(async (silent = false) => {
    if (!silent) setMovementsLoading(true);
    try {
      const res = await storeItemAPI.getMovements();
      setMovements(res.data?.data?.movements || []);
    } catch {
      if (!silent) toast.error('Failed to load stock movements.');
    } finally {
      if (!silent) setMovementsLoading(false);
    }
  }, []);

  const fetchInventoryReport = useCallback(async () => {
    setReportLoading(true);
    try {
      const params = {};
      if (reportFrom) params.from = reportFrom;
      if (reportTo) params.to = reportTo;
      const res = await storeItemAPI.getMonthlyReport(params);
      setInventoryReport(res.data?.data);
    } catch {
      toast.error('Failed to load inventory report.');
    } finally {
      setReportLoading(false);
    }
  }, [reportFrom, reportTo]);

  useEffect(() => {
    if (section !== 'inventory') return;
    if (invSubTab === 'items') fetchItems();
    if (invSubTab === 'movements') { fetchItems(); fetchMovements(); }
    if (invSubTab === 'report') fetchInventoryReport();
  }, [section, invSubTab, fetchItems, fetchMovements, fetchInventoryReport]);

  // Keep the shared portal in sync: other receptionists' changes show up here
  // automatically via quiet polling, without stealing focus from open forms.
  useEffect(() => {
    const poll = () => {
      if (document.hidden) return;
      if (section === 'transactions') fetchTransactions(true);
      if (section === 'inventory') {
        if (invSubTab === 'items') fetchItems(true);
        if (invSubTab === 'movements') { fetchItems(true); fetchMovements(true); }
      }
    };
    const interval = setInterval(poll, 15000);
    window.addEventListener('focus', poll);
    document.addEventListener('visibilitychange', poll);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', poll);
      document.removeEventListener('visibilitychange', poll);
    };
  }, [section, invSubTab, fetchTransactions, fetchItems, fetchMovements]);

  const openCreateItem = () => {
    setEditingItem(null);
    setItemForm(emptyItemForm);
    setItemModalOpen(true);
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      itemName: item.itemName || '',
      unit: item.unit || '',
      openingStock: item.openingStock ?? 0,
      openingStockDate: item.openingStockDate ? item.openingStockDate.slice(0, 10) : '',
      reorderLevel: item.reorderLevel ?? '',
    });
    setItemModalOpen(true);
  };

  const handleItemSubmit = async (e) => {
    e.preventDefault();
    setSavingItem(true);
    try {
      const payload = {
        itemName: itemForm.itemName,
        unit: itemForm.unit,
        openingStock: Number(itemForm.openingStock) || 0,
        openingStockDate: itemForm.openingStockDate || undefined,
        reorderLevel: itemForm.reorderLevel !== '' ? Number(itemForm.reorderLevel) : undefined,
      };
      if (editingItem) {
        await storeItemAPI.update(editingItem._id, payload);
        toast.success('Item updated.');
      } else {
        await storeItemAPI.create(payload);
        toast.success('Item added.');
      }
      setItemModalOpen(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save item.');
    } finally {
      setSavingItem(false);
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm('Delete this item? This is only possible if it has no stock movements.')) return;
    try {
      await storeItemAPI.delete(id);
      setItems((prev) => prev.filter((i) => i._id !== id));
      toast.success('Item deleted.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete item.');
    }
  };

  // ── Inventory & Stock: Movements ────────────────────────────────
  const handleMovementSubmit = async (e) => {
    e.preventDefault();
    if (!movementForm.item || !movementForm.quantity) {
      toast.error('Please select an item and enter a quantity.');
      return;
    }
    setSavingMovement(true);
    try {
      await storeItemAPI.addMovement({
        ...movementForm,
        quantity: Number(movementForm.quantity),
        date: movementForm.date || undefined,
      });
      toast.success('Stock movement recorded.');
      setMovementModalOpen(false);
      setMovementForm(emptyMovementForm);
      fetchMovements();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to record movement.');
    } finally {
      setSavingMovement(false);
    }
  };

  const handleDeleteMovement = async (id) => {
    if (!window.confirm('Delete this stock movement?')) return;
    try {
      await storeItemAPI.deleteMovement(id);
      setMovements((prev) => prev.filter((m) => m._id !== id));
      toast.success('Movement deleted.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete movement.');
    }
  };

  return (
    <AdminLayout>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#1e3a8a]">Store Management</h2>
          <p className="text-slate-500 text-sm mt-1">
            Purchases, returnable items and issued (non-paid) items with full history
          </p>
        </div>
        {canEdit && section === 'transactions' && (
          <button onClick={() => { setForm(emptyForm); setModalOpen(true); }}
            className="flex items-center gap-2 px-4 py-2.5 text-white rounded-xl text-sm font-semibold shadow-md hover:shadow-lg transition-all"
            style={{ background: 'linear-gradient(135deg, #1e3a8a, #2563EB)' }}>
            <FiPlus className="w-4 h-4" /> New Record
          </button>
        )}
      </div>

      {/* Section switcher */}
      <div className="flex gap-2 mb-6 bg-white border border-blue-100 rounded-2xl p-1.5 shadow-sm w-fit">
        <button onClick={() => setSection('transactions')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
            section === 'transactions' ? 'bg-[#1D4ED8] text-white' : 'text-slate-500 hover:bg-blue-50'
          }`}>
          <FiShoppingBag className="w-4 h-4" /> Transactions
        </button>
        <button onClick={() => setSection('inventory')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
            section === 'inventory' ? 'bg-[#1D4ED8] text-white' : 'text-slate-500 hover:bg-blue-50'
          }`}>
          <FiPackage className="w-4 h-4" /> Inventory &amp; Stock
        </button>
      </div>

      {section === 'transactions' && (
      <>
      {/* Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {TABS.map((tab) => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeTab === tab.key
                ? 'bg-[#1D4ED8] text-white shadow-md'
                : 'bg-white text-slate-500 border border-blue-100 hover:bg-blue-50'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-blue-100 p-4 mb-6 flex gap-4 items-center flex-wrap shadow-sm">
        <div className="relative flex-1 min-w-[220px]">
          <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input type="text" placeholder="Search by item or person name..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-blue-100 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200 text-slate-700 placeholder-blue-200"
          />
        </div>
        <select value={personTypeFilter} onChange={(e) => setPersonTypeFilter(e.target.value)}
          className="px-4 py-2.5 border border-blue-100 rounded-xl text-sm text-slate-600 focus:outline-none focus:border-blue-400">
          <option value="">All persons</option>
          {PERSON_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <div className="text-sm text-slate-400 flex-shrink-0">
          {transactions.length} record{transactions.length !== 1 ? 's' : ''}
        </div>
      </div>

      {loading ? <LoadingSpinner /> : (
        <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-50/50 border-b border-blue-100">
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Item</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Person</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Qty</th>
                  {activeTab === 'purchase' && (
                    <>
                      <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Price</th>
                      <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Paid</th>
                    </>
                  )}
                  {activeTab === 'returnable' && (
                    <>
                      <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden md:table-cell">Expected Return</th>
                      <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Status</th>
                    </>
                  )}
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden sm:table-cell">Date</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden lg:table-cell">Remarks</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((t, idx) => (
                  <motion.tr key={t._id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.03 }}
                    className="border-b border-blue-50 hover:bg-blue-50/30 transition-colors">
                    <td className="px-5 py-4 font-semibold text-[#1e3a8a]">{t.itemName}</td>
                    <td className="px-5 py-4">
                      <span className="text-slate-700 block">{t.personName}</span>
                      <span className="text-slate-400 text-xs">{t.personType}</span>
                    </td>
                    <td className="px-5 py-4 text-slate-600">{t.quantity}</td>
                    {activeTab === 'purchase' && (
                      <>
                        <td className="px-5 py-4 text-slate-700 font-medium whitespace-nowrap">
                          {t.price !== undefined && t.price !== null ? `Rs. ${Number(t.price).toLocaleString()}` : '—'}
                        </td>
                        <td className="px-5 py-4">
                          <button onClick={() => canEdit && handleTogglePaid(t._id)} disabled={!canEdit}
                            className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                              t.isPaid
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : 'bg-amber-50 text-amber-700 border-amber-200'
                            } ${canEdit ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}>
                            {t.isPaid ? 'Paid' : 'Unpaid'}
                          </button>
                        </td>
                      </>
                    )}
                    {activeTab === 'returnable' && (
                      <>
                        <td className="px-5 py-4 hidden md:table-cell text-slate-500 text-xs whitespace-nowrap">
                          {t.expectedReturnDate ? formatDate(t.expectedReturnDate) : '—'}
                        </td>
                        <td className="px-5 py-4">
                          {t.returnStatus === 'Returned' ? (
                            <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-semibold whitespace-nowrap">
                              Returned {t.actualReturnDate ? `· ${formatDate(t.actualReturnDate)}` : ''}
                            </span>
                          ) : (
                            <span className="px-2.5 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-xs font-semibold">
                              Not Returned
                            </span>
                          )}
                        </td>
                      </>
                    )}
                    <td className="px-5 py-4 hidden sm:table-cell text-slate-400 text-xs whitespace-nowrap">
                      {formatDate(activeTab === 'returnable' ? t.issueDate || t.date : t.date)}
                    </td>
                    <td className="px-5 py-4 hidden lg:table-cell text-slate-500 max-w-[180px]">
                      <p className="truncate">{t.remarks || '—'}</p>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        {canEdit && activeTab === 'returnable' && t.returnStatus !== 'Returned' && (
                          <button onClick={() => handleMarkReturned(t._id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-xs font-semibold transition-colors">
                            <FiCheckCircle className="w-3.5 h-3.5" /> Mark Returned
                          </button>
                        )}
                        {canEdit && (
                          <button onClick={() => handleDelete(t._id)}
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {paginated.length === 0 && (
            <div className="text-center py-16">
              <FiShoppingBag className="w-12 h-12 text-blue-200 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No records in this section yet</p>
              {canEdit && (
                <p className="text-slate-400 text-sm mt-1">Click "New Record" to add the first one.</p>
              )}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 p-4 border-t border-blue-50">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                className="p-2 rounded-xl border border-blue-100 hover:bg-blue-50 disabled:opacity-40 transition-colors">
                <FiChevronLeft className="w-4 h-4 text-slate-500" />
              </button>
              <span className="text-sm text-slate-500 px-3">Page {page} of {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="p-2 rounded-xl border border-blue-100 hover:bg-blue-50 disabled:opacity-40 transition-colors">
                <FiChevronRight className="w-4 h-4 text-slate-500" />
              </button>
            </div>
          )}
        </div>
      )}
      </>
      )}

      {section === 'inventory' && (
        <div>
          {/* Inventory sub-tabs */}
          <div className="flex gap-2 mb-6 flex-wrap">
            {[
              { key: 'items', label: 'Items' },
              { key: 'movements', label: 'Stock Movements' },
              { key: 'report', label: 'Inventory Report' },
            ].map((tab) => (
              <button key={tab.key} onClick={() => setInvSubTab(tab.key)}
                className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  invSubTab === tab.key
                    ? 'bg-[#1D4ED8] text-white shadow-md'
                    : 'bg-white text-slate-500 border border-blue-100 hover:bg-blue-50'
                }`}>
                {tab.label}
              </button>
            ))}
          </div>

          {invSubTab === 'items' && (
            <div>
              {canEdit && (
                <div className="flex justify-end mb-4">
                  <button onClick={openCreateItem}
                    className="flex items-center gap-2 px-4 py-2.5 text-white rounded-xl text-sm font-semibold shadow-md hover:shadow-lg transition-all"
                    style={{ background: 'linear-gradient(135deg, #1e3a8a, #2563EB)' }}>
                    <FiPlus className="w-4 h-4" /> Add Item
                  </button>
                </div>
              )}
              {itemsLoading ? <LoadingSpinner /> : (
                <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-blue-50/50 border-b border-blue-100">
                          <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Item</th>
                          <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Unit</th>
                          <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Opening Stock</th>
                          <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden md:table-cell">Reorder Level</th>
                          <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Status</th>
                          {canEdit && <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item) => (
                          <tr key={item._id} className="border-b border-blue-50 hover:bg-blue-50/30 transition-colors">
                            <td className="px-5 py-4 font-semibold text-[#1e3a8a]">{item.itemName}</td>
                            <td className="px-5 py-4 text-slate-600">{item.unit}</td>
                            <td className="px-5 py-4 text-slate-600">{item.openingStock}</td>
                            <td className="px-5 py-4 hidden md:table-cell text-slate-500">{item.reorderLevel ?? '—'}</td>
                            <td className="px-5 py-4">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
                                item.isActive ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'
                              }`}>
                                {item.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            {canEdit && (
                              <td className="px-5 py-4">
                                <div className="flex items-center gap-2">
                                  <button onClick={() => openEditItem(item)}
                                    className="p-1.5 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
                                    <FiEdit2 className="w-4 h-4" />
                                  </button>
                                  <button onClick={() => handleDeleteItem(item._id)}
                                    className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                                    <FiTrash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {items.length === 0 && (
                    <div className="text-center py-16">
                      <FiPackage className="w-12 h-12 text-blue-200 mx-auto mb-3" />
                      <p className="text-slate-500 font-medium">No items registered yet</p>
                      {canEdit && <p className="text-slate-400 text-sm mt-1">Click "Add Item" to register the first item.</p>}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {invSubTab === 'movements' && (
            <div>
              {canEdit && (
                <div className="flex justify-end mb-4">
                  <button onClick={() => { setMovementForm(emptyMovementForm); setMovementModalOpen(true); }}
                    className="flex items-center gap-2 px-4 py-2.5 text-white rounded-xl text-sm font-semibold shadow-md hover:shadow-lg transition-all"
                    style={{ background: 'linear-gradient(135deg, #1e3a8a, #2563EB)' }}>
                    <FiPlus className="w-4 h-4" /> Record Movement
                  </button>
                </div>
              )}
              {movementsLoading ? <LoadingSpinner /> : (
                <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-blue-50/50 border-b border-blue-100">
                          <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Item</th>
                          <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Type</th>
                          <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Quantity</th>
                          <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden md:table-cell">Issued To</th>
                          <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden sm:table-cell">Date</th>
                          {canEdit && <th className="px-4 py-3" />}
                        </tr>
                      </thead>
                      <tbody>
                        {movements.map((m) => (
                          <tr key={m._id} className="border-b border-blue-50 hover:bg-blue-50/30 transition-colors">
                            <td className="px-5 py-4 font-semibold text-[#1e3a8a]">{m.item?.itemName || '—'} <span className="text-slate-400 font-normal">{m.item?.unit}</span></td>
                            <td className="px-5 py-4">
                              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                                m.type === 'Received' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                              }`}>
                                {m.type === 'Received' ? <FiArrowDownCircle className="w-3 h-3" /> : <FiArrowUpCircle className="w-3 h-3" />}
                                {m.type}
                              </span>
                            </td>
                            <td className="px-5 py-4 text-slate-600">{m.quantity}</td>
                            <td className="px-5 py-4 hidden md:table-cell text-slate-500">{m.issuedTo || '—'}</td>
                            <td className="px-5 py-4 hidden sm:table-cell text-slate-400 text-xs whitespace-nowrap">{formatDate(m.date)}</td>
                            {canEdit && (
                              <td className="px-5 py-4">
                                <button onClick={() => handleDeleteMovement(m._id)}
                                  className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                                  <FiTrash2 className="w-4 h-4" />
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {movements.length === 0 && (
                    <div className="text-center py-16">
                      <FiPackage className="w-12 h-12 text-blue-200 mx-auto mb-3" />
                      <p className="text-slate-500 font-medium">No stock movements recorded yet</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {invSubTab === 'report' && (
            <div>
              <div className="bg-white rounded-2xl border border-blue-100 p-4 mb-6 flex flex-wrap gap-4 items-end shadow-sm">
                <div>
                  <label className={labelClass}>From</label>
                  <input type="date" className={inputClass} value={reportFrom}
                    onChange={(e) => setReportFrom(e.target.value)} />
                </div>
                <div>
                  <label className={labelClass}>To</label>
                  <input type="date" className={inputClass} value={reportTo}
                    onChange={(e) => setReportTo(e.target.value)} />
                </div>
                <button onClick={fetchInventoryReport}
                  className="flex items-center gap-2 px-4 py-2.5 text-white rounded-xl text-sm font-semibold shadow-md hover:shadow-lg transition-all"
                  style={{ background: 'linear-gradient(135deg, #1e3a8a, #2563EB)' }}>
                  <FiBarChart2 className="w-4 h-4" /> Generate Report
                </button>
                {(reportFrom || reportTo) && (
                  <button onClick={() => { setReportFrom(''); setReportTo(''); }}
                    className="px-4 py-2.5 border border-blue-100 rounded-xl text-sm font-semibold text-slate-500 hover:bg-blue-50 transition-colors">
                    Clear
                  </button>
                )}
              </div>

              {reportLoading ? <LoadingSpinner /> : inventoryReport && (
                <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-blue-50/50 border-b border-blue-100">
                          <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Item</th>
                          <th className="text-right px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Opening Stock</th>
                          <th className="text-right px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Received</th>
                          <th className="text-right px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Issued</th>
                          <th className="text-right px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Current Stock</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inventoryReport.items.map((row) => (
                          <tr key={row.itemId} className="border-b border-blue-50 hover:bg-blue-50/30 transition-colors">
                            <td className="px-5 py-4 font-semibold text-[#1e3a8a]">{row.itemName} <span className="text-slate-400 font-normal text-xs">{row.unit}</span></td>
                            <td className="px-5 py-4 text-right text-slate-600">{row.openingStock}</td>
                            <td className="px-5 py-4 text-right text-slate-600">{row.received}</td>
                            <td className="px-5 py-4 text-right text-slate-600">{row.issued}</td>
                            <td className="px-5 py-4 text-right font-semibold text-[#1e3a8a]">{row.currentStock}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-blue-50/70 border-t-2 border-blue-100">
                          <td className="px-5 py-4 font-bold text-[#1e3a8a]">Total</td>
                          <td className="px-5 py-4 text-right font-bold text-[#1e3a8a]">{inventoryReport.totals.openingStock}</td>
                          <td className="px-5 py-4 text-right font-bold text-[#1e3a8a]">{inventoryReport.totals.received}</td>
                          <td className="px-5 py-4 text-right font-bold text-[#1e3a8a]">{inventoryReport.totals.issued}</td>
                          <td className="px-5 py-4 text-right font-bold text-[#1e3a8a]">{inventoryReport.totals.currentStock}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  {inventoryReport.items.length === 0 && (
                    <div className="text-center py-16">
                      <FiBarChart2 className="w-12 h-12 text-blue-200 mx-auto mb-3" />
                      <p className="text-slate-500 font-medium">No active items to report on</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Item Modal */}
      <Modal isOpen={itemModalOpen} onClose={() => setItemModalOpen(false)}
        title={editingItem ? 'Edit Item' : 'Add Item'} size="md">
        <form onSubmit={handleItemSubmit} className="space-y-4">
          <div>
            <label className={labelClass}>Item Name *</label>
            <input className={inputClass} required value={itemForm.itemName}
              onChange={(e) => setItemForm({ ...itemForm, itemName: e.target.value })}
              placeholder="e.g. A4 Paper Ream" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Unit *</label>
              <input className={inputClass} required value={itemForm.unit}
                onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })}
                placeholder="e.g. pcs, box, ream" />
            </div>
            <div>
              <label className={labelClass}>Reorder Level</label>
              <input className={inputClass} type="number" min="0" value={itemForm.reorderLevel}
                onChange={(e) => setItemForm({ ...itemForm, reorderLevel: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Opening Stock *</label>
              <input className={inputClass} type="number" min="0" required value={itemForm.openingStock}
                onChange={(e) => setItemForm({ ...itemForm, openingStock: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>Opening Stock Date</label>
              <input className={inputClass} type="date" value={itemForm.openingStockDate}
                onChange={(e) => setItemForm({ ...itemForm, openingStockDate: e.target.value })} />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setItemModalOpen(false)}
              className="px-5 py-2.5 border border-blue-100 rounded-xl text-sm font-semibold text-slate-500 hover:bg-blue-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={savingItem}
              className="px-5 py-2.5 text-white rounded-xl text-sm font-semibold shadow-md disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #1e3a8a, #2563EB)' }}>
              {savingItem ? 'Saving...' : editingItem ? 'Update Item' : 'Add Item'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Record Movement Modal */}
      <Modal isOpen={movementModalOpen} onClose={() => setMovementModalOpen(false)}
        title="Record Stock Movement" size="md">
        <form onSubmit={handleMovementSubmit} className="space-y-4">
          <div>
            <label className={labelClass}>Item *</label>
            <select className={inputClass} required value={movementForm.item}
              onChange={(e) => setMovementForm({ ...movementForm, item: e.target.value })}>
              <option value="">Select an item...</option>
              {items.filter((i) => i.isActive).map((i) => (
                <option key={i._id} value={i._id}>{i.itemName} ({i.unit})</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Type *</label>
              <select className={inputClass} value={movementForm.type}
                onChange={(e) => setMovementForm({ ...movementForm, type: e.target.value })}>
                <option value="Received">Received</option>
                <option value="Issued">Issued</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Quantity *</label>
              <input className={inputClass} type="number" min="1" required value={movementForm.quantity}
                onChange={(e) => setMovementForm({ ...movementForm, quantity: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Date</label>
              <input className={inputClass} type="date" value={movementForm.date}
                onChange={(e) => setMovementForm({ ...movementForm, date: e.target.value })} />
            </div>
            {movementForm.type === 'Issued' && (
              <div>
                <label className={labelClass}>Issued To</label>
                <input className={inputClass} value={movementForm.issuedTo}
                  onChange={(e) => setMovementForm({ ...movementForm, issuedTo: e.target.value })}
                  placeholder="e.g. Class 5, Office" />
              </div>
            )}
          </div>
          <div>
            <label className={labelClass}>Remarks</label>
            <textarea className={inputClass} rows={2} value={movementForm.remarks}
              onChange={(e) => setMovementForm({ ...movementForm, remarks: e.target.value })} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setMovementModalOpen(false)}
              className="px-5 py-2.5 border border-blue-100 rounded-xl text-sm font-semibold text-slate-500 hover:bg-blue-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={savingMovement}
              className="px-5 py-2.5 text-white rounded-xl text-sm font-semibold shadow-md disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #1e3a8a, #2563EB)' }}>
              {savingMovement ? 'Saving...' : 'Record Movement'}
            </button>
          </div>
        </form>
      </Modal>

      {/* New Record Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}
        title={`New ${TABS.find((t) => t.key === activeTab)?.label} Record`} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Item Name *</label>
              <input className={inputClass} required value={form.itemName}
                onChange={(e) => setForm({ ...form, itemName: e.target.value })}
                placeholder={activeTab === 'purchase' ? 'e.g. Homework Diary' : activeTab === 'returnable' ? 'e.g. Football' : 'e.g. Marker'} />
            </div>
            <div>
              <label className={labelClass}>{activeTab === 'purchase' ? 'Person Name *' : 'Taken By *'}</label>
              <input className={inputClass} required value={form.personName}
                onChange={(e) => setForm({ ...form, personName: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>Person Type *</label>
              <select className={inputClass} value={form.personType}
                onChange={(e) => setForm({ ...form, personType: e.target.value })}>
                {PERSON_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Quantity *</label>
              <input className={inputClass} type="number" min="1" required value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
            </div>

            {activeTab === 'purchase' && (
              <>
                <div>
                  <label className={labelClass}>Price (Rs.) *</label>
                  <input className={inputClass} type="number" min="0" step="0.01" required value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })} />
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2.5 text-sm font-semibold text-slate-600 cursor-pointer">
                    <input type="checkbox" checked={form.isPaid}
                      onChange={(e) => setForm({ ...form, isPaid: e.target.checked })}
                      className="w-4 h-4 accent-blue-600" />
                    Paid
                  </label>
                </div>
              </>
            )}

            {activeTab === 'returnable' && (
              <>
                <div>
                  <label className={labelClass}>Issue Date</label>
                  <input className={inputClass} type="date" value={form.issueDate}
                    onChange={(e) => setForm({ ...form, issueDate: e.target.value })} />
                </div>
                <div>
                  <label className={labelClass}>Expected Return Date</label>
                  <input className={inputClass} type="date" value={form.expectedReturnDate}
                    onChange={(e) => setForm({ ...form, expectedReturnDate: e.target.value })} />
                </div>
              </>
            )}

            {activeTab !== 'returnable' && (
              <div>
                <label className={labelClass}>Date</label>
                <input className={inputClass} type="date" value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
            )}
          </div>
          <div>
            <label className={labelClass}>Remarks</label>
            <textarea className={inputClass} rows={2} value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setModalOpen(false)}
              className="px-5 py-2.5 border border-blue-100 rounded-xl text-sm font-semibold text-slate-500 hover:bg-blue-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="px-5 py-2.5 text-white rounded-xl text-sm font-semibold shadow-md disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #1e3a8a, #2563EB)' }}>
              {saving ? 'Saving...' : 'Save Record'}
            </button>
          </div>
        </form>
      </Modal>
    </AdminLayout>
  );
};

export default AdminStore;
